import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiVideo,
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiClock,
  FiUser,
  FiBook,
  FiLayers,
  FiPackage,
  FiLink,
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiZap,
  FiExternalLink,
} from "react-icons/fi";
import Button from "../../components/Button/Button";
import adminApi from "../../api/adminApi";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Modal from "../../components/Modal/Modal";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import "./LectureDetail.css";

const LectureDetail = () => {
  const { lectureId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lecture, setLecture] = useState(null);
  const [batch, setBatch] = useState(null);
  const [module, setModule] = useState(null);
  const [course, setCourse] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    topic: "",
    description: "",
    startTime: "",
    endTime: "",
    meetingLink: "",
    recordingLink: "",
    module: "",
    teacher: "",
    status: "scheduled",
  });
  const [recordingLink, setRecordingLink] = useState("");
  const [statusUpdate, setStatusUpdate] = useState("scheduled");
  const [availableModules, setAvailableModules] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);

  useEffect(() => {
    loadLecture();
  }, [lectureId]);

  console.log('lecture ===>>', lecture);

  const loadLecture = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get(`/lectures/${lectureId}`);
      const lectureData = response.data;
      setLecture(lectureData);

      // Use populated data from lecture response if available
      // The backend populates: module.batch.course, teacher.user, and batch
      
      // Extract batch data (can be from populated batch or module.batch)
      let batchData = null;
      if (lectureData.batch) {
        batchData =
          typeof lectureData.batch === "object"
            ? lectureData.batch
            : { _id: lectureData.batch };
      } else if (
        lectureData.module &&
        typeof lectureData.module === "object" &&
        lectureData.module.batch
      ) {
        batchData = lectureData.module.batch;
      }

      // Extract course data (can be from batch.course or module.batch.course)
      let courseData = null;
      if (batchData?.course) {
        courseData =
          typeof batchData.course === "object"
            ? batchData.course
            : { _id: batchData.course };
      } else if (
        lectureData.module &&
        typeof lectureData.module === "object" &&
        lectureData.module.batch?.course
      ) {
        courseData = lectureData.module.batch.course;
      }

      // Extract module data
      let moduleData = null;
      if (lectureData.module) {
        moduleData =
          typeof lectureData.module === "object"
            ? lectureData.module
            : { _id: lectureData.module };
      }

      // Set the extracted data
      if (moduleData) {
        setModule(moduleData);
      }
      if (batchData) {
        setBatch(batchData);
        // Load full batch details if we only have basic data
        if (batchData._id && !batchData.batchName) {
          try {
            const batchResponse = await adminApi.get(
              `/batches/${batchData._id}`
            );
            setBatch(batchResponse.data);
          } catch (err) {
            console.error("Failed to load batch:", err);
          }
        }
      }
      if (courseData) {
        setCourse(courseData);
        // Load full course details if we only have basic data
        if (courseData._id && !courseData.title) {
          try {
            const courseResponse = await adminApi.get(
              `/courses/${courseData._id}`
            );
            setCourse(courseResponse.data);
          } catch (err) {
            console.error("Failed to load course:", err);
          }
        }
      }

      // Load modules for this batch if we have batch ID
      if (batchData?._id) {
        try {
          const modulesResponse = await adminApi.get(
            `/batches/${batchData._id}/modules`
          );
          const modulesList =
            modulesResponse.data?.modules ||
            modulesResponse.data?.data ||
            modulesResponse.data ||
            [];
          setAvailableModules(modulesList);
        } catch (err) {
          console.error("Failed to load modules:", err);
        }
      }

      // Load teacher - use populated data if available
      if (lectureData.teacher) {
        if (typeof lectureData.teacher === "object") {
          // Teacher data is already populated in the lecture response
          setTeacher(lectureData.teacher);
        } else {
          // If teacher is just an ID, try to get via instructors endpoint
          const teacherId = lectureData.teacher;
          try {
            // Try to get via instructors endpoint using teacher ID as user ID
            // Note: This assumes the teacher ID might be a user ID
            const instructorResponse = await adminApi.get(
              `/instructors/${teacherId}`
            );
            setTeacher(instructorResponse.data);
          } catch (err) {
            console.error("Failed to load teacher:", err);
            // If fetch fails, create a minimal teacher object
            setTeacher({ _id: teacherId });
          }
        }
      }

      // Load available teachers
      loadAvailableTeachers();

      // Set form data
      setFormData({
        topic: lectureData.topic || "",
        description: lectureData.description || "",
        startTime: lectureData.startTime
          ? new Date(lectureData.startTime).toISOString().slice(0, 16)
          : "",
        endTime: lectureData.endTime
          ? new Date(lectureData.endTime).toISOString().slice(0, 16)
          : "",
        meetingLink: lectureData.meetingLink || "",
        recordingLink: lectureData.recordingLink || "",
        module:
          lectureData.module && typeof lectureData.module === "object"
            ? lectureData.module._id
            : lectureData.module || "",
        teacher:
          typeof lectureData.teacher === "object"
            ? lectureData.teacher._id
            : lectureData.teacher || "",
        status: lectureData.status || "scheduled",
      });
      setRecordingLink(lectureData.recordingLink || "");
      setStatusUpdate(lectureData.status || "scheduled");
    } catch (err) {
      console.error("Failed to load lecture:", err);
      setError(err.response?.data?.message || "Failed to load lecture");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTeachers = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "mentor", limit: 1000 },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setAvailableTeachers(usersList);
    } catch (err) {
      console.error("Failed to load teachers:", err);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const lectureData = {
        topic: formData.topic,
        description: formData.description,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        meetingLink: formData.meetingLink,
        recordingLink: formData.recordingLink,
      };

      if (formData.module) {
        lectureData.module = formData.module;
      }
      if (formData.teacher) {
        lectureData.teacher = formData.teacher;
      }
      if (formData.status) {
        lectureData.status = formData.status;
      }

      await adminApi.put(`/lectures/${lectureId}`, lectureData);
      setShowEditModal(false);
      loadLecture();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update lecture");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await adminApi.delete(`/lectures/${lectureId}`);
      navigate("/lectures");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete lecture");
      setDeleteLoading(false);
    }
  };

  const handleAttachRecording = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await adminApi.patch(`/lectures/${lectureId}/recording`, {
        recordingLink,
      });
      setShowRecordingModal(false);
      loadLecture();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to attach recording");
    } finally {
      setEditLoading(false);
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await adminApi.put(`/lectures/${lectureId}`, {
        status: statusUpdate,
      });
      setShowStatusModal(false);
      loadLecture();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    } finally {
      setEditLoading(false);
    }
  };

  const handleJoinGoogleMeet = () => {
    if (lecture?.meetingLink) {
      window.open(lecture.meetingLink, "_blank");
    } else {
      setError("No meeting link available");
    }
  };

  const getStatusBadge = () => {
    if (!lecture) return "scheduled";
    if (lecture.status === "cancelled") return "cancelled";
    if (lecture.status === "completed") return "completed";
    if (!lecture.startTime) return "scheduled";
    const now = new Date();
    const start = new Date(lecture.startTime);
    const end = lecture.endTime ? new Date(lecture.endTime) : null;
    if (end && now > end) return "completed";
    if (start <= now && (!end || now <= end)) return "active";
    return "upcoming";
  };

  if (loading) {
    return <div className="detail-loading">Loading lecture details...</div>;
  }

  if (error && !lecture) {
    return (
      <div className="detail-error">
        <p>{error}</p>
        <Button onClick={() => navigate("/lectures")}>Back to Lectures</Button>
      </div>
    );
  }

  return (
    <div className="lecture-detail-page">
      <div className="detail-header">
        <Button
          variant="secondary"
          size="small"
          onClick={() => navigate("/lectures")}
        >
          <FiArrowLeft size={16} />
          Back to Lectures
        </Button>
        <div className="header-actions">
          <Button
            variant="secondary"
            onClick={() => setShowStatusModal(true)}
          >
            <FiEdit size={18} />
            Update Status
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setFormData({
                topic: lecture.topic || "",
                description: lecture.description || "",
                startTime: lecture.startTime
                  ? new Date(lecture.startTime).toISOString().slice(0, 16)
                  : "",
                endTime: lecture.endTime
                  ? new Date(lecture.endTime).toISOString().slice(0, 16)
                  : "",
                meetingLink: lecture.meetingLink || "",
                recordingLink: lecture.recordingLink || "",
                module:
                  typeof lecture.module === "object"
                    ? lecture.module._id
                    : lecture.module || "",
                teacher:
                  typeof lecture.teacher === "object"
                    ? lecture.teacher._id
                    : lecture.teacher || "",
                status: lecture.status || "scheduled",
              });
              setShowEditModal(true);
            }}
          >
            <FiEdit size={18} />
            Edit Lecture
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            <FiTrash2 size={18} />
            Delete
          </Button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="detail-content">
        <div className="detail-main">
          <div className="detail-card">
            <div className="card-header">
              <h1>{lecture?.topic || "Untitled Lecture"}</h1>
              <StatusBadge status={getStatusBadge()} />
            </div>
            {lecture?.description && (
              <div className="card-section">
                <h3>Description</h3>
                <p>{lecture.description}</p>
              </div>
            )}

            <div className="card-section">
              <h3>Schedule</h3>
              <div className="info-grid">
                <div className="info-item">
                  <FiCalendar size={18} />
                  <div>
                    <label>Start Time</label>
                    <p>
                      {lecture?.startTime
                        ? new Date(lecture.startTime).toLocaleString()
                        : "—"}
                    </p>
                  </div>
                </div>
                <div className="info-item">
                  <FiClock size={18} />
                  <div>
                    <label>End Time</label>
                    <p>
                      {lecture?.endTime
                        ? new Date(lecture.endTime).toLocaleString()
                        : "—"}
                    </p>
                  </div>
                </div>
                {lecture?.startTime && lecture?.endTime && (
                  <div className="info-item">
                    <FiClock size={18} />
                    <div>
                      <label>Duration</label>
                      <p>
                        {Math.round(
                          (new Date(lecture.endTime) -
                            new Date(lecture.startTime)) /
                            60000
                        )}{" "}
                        minutes
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card-section">
              <h3>Relationships</h3>
              <div className="breadcrumb-nav">
                {course && (
                  <Link to={`/courses/${course._id}`} className="breadcrumb-link">
                    <FiBook size={16} />
                    {course.title}
                  </Link>
                )}
                {batch && (
                  <>
                    <span className="breadcrumb-separator">→</span>
                    <Link
                      to={`/batches/${batch._id}`}
                      className="breadcrumb-link"
                    >
                      <FiLayers size={16} />
                      {batch.batchName}
                    </Link>
                  </>
                )}
                {module && (
                  <>
                    <span className="breadcrumb-separator">→</span>
                    <Link
                      to={`/modules/${module._id}`}
                      className="breadcrumb-link"
                    >
                      <FiPackage size={16} />
                      {module.title}
                      {module.isOpen ? (
                        <span style={{ marginLeft: "8px", color: "var(--success, #22c55e)", fontSize: "0.75rem" }}>
                          ✓ Open
                        </span>
                      ) : (
                        <span style={{ marginLeft: "8px", color: "var(--warning, #f59e0b)", fontSize: "0.75rem" }}>
                          🔒 Closed
                        </span>
                      )}
                    </Link>
                  </>
                )}
                {!module && (
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.875rem", marginTop: "var(--space-2)" }}>
                    No module assigned to this lecture.
                  </p>
                )}
              </div>
              {module && !module.isOpen && (
                <div
                  style={{
                    padding: "var(--space-3)",
                    background: "rgba(251, 191, 36, 0.1)",
                    border: "1px solid rgba(251, 191, 36, 0.3)",
                    borderRadius: "var(--radius-md)",
                    marginTop: "var(--space-3)",
                    fontSize: "0.875rem",
                  }}
                >
                  <strong style={{ color: "var(--warning, #f59e0b)" }}>
                    ⚠️ Module is Closed
                  </strong>
                  <p style={{ margin: "var(--space-2) 0 0 0", color: "var(--text-secondary)" }}>
                    Students CANNOT see this lecture because the module is closed.
                    <Link
                      to={`/batches/${batch?._id || lecture?.batch?._id || lecture?.batch}`}
                      style={{
                        color: "var(--primary, #3b82f6)",
                        textDecoration: "none",
                        fontWeight: 500,
                        marginLeft: "4px",
                      }}
                    >
                      Open the module
                    </Link>{" "}
                    from the batch detail page to make it visible to students.
                  </p>
                </div>
              )}
              {!module && (
                <div
                  style={{
                    padding: "var(--space-3)",
                    background: "rgba(251, 191, 36, 0.1)",
                    border: "1px solid rgba(251, 191, 36, 0.3)",
                    borderRadius: "var(--radius-md)",
                    marginTop: "var(--space-3)",
                    fontSize: "0.875rem",
                  }}
                >
                  <strong style={{ color: "var(--warning, #f59e0b)" }}>
                    ⚠️ No Module Assigned
                  </strong>
                  <p style={{ margin: "var(--space-2) 0 0 0", color: "var(--text-secondary)" }}>
                    This lecture is not assigned to any module. Students will NOT be able to see this lecture.
                    Edit this lecture and assign it to a module, then make sure that module is open.
                  </p>
                </div>
              )}
            </div>

            <div className="card-section">
              <h3>Teacher</h3>
              {teacher ? (
                <div className="info-item">
                  <FiUser size={18} />
                  <div>
                    <label>
                      {teacher.user?.firstName || teacher.firstName || ""}{" "}
                      {teacher.user?.lastName || teacher.lastName || ""}
                    </label>
                    <p>{teacher.user?.email || teacher.email || "—"}</p>
                  </div>
                </div>
              ) : (
                <p>No teacher assigned</p>
              )}
            </div>

            <div className="card-section">
              <h3>Google Meet</h3>
              {lecture?.meetingLink ? (
                <div>
                  <div style={{
                    padding: '12px 16px',
                    background: '#f0fdf4',
                    border: '1px solid #22c55e',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    color: '#15803d',
                    marginBottom: "var(--space-3)",
                    wordBreak: "break-all"
                  }}>
                    {lecture.meetingLink}
                  </div>
                  <Button
                    onClick={handleJoinGoogleMeet}
                    variant="primary"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiVideo size={16} />
                    Join Google Meet
                  </Button>
                </div>
              ) : (
                <p className="text-muted">No meeting link available</p>
              )}
            </div>

            {lecture?.recordingLink && (
              <div className="card-section">
                <h3>Recording</h3>
                <a
                  href={lecture.recordingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  <FiLink size={16} />
                  {lecture.recordingLink}
                </a>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    setRecordingLink(lecture.recordingLink || "");
                    setShowRecordingModal(true);
                  }}
                  style={{ marginTop: "var(--space-3)" }}
                >
                  Update Recording
                </Button>
              </div>
            )}

            {!lecture?.recordingLink && (
              <div className="card-section">
                <h3>Recording</h3>
                <p className="text-muted">No recording attached</p>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    setRecordingLink("");
                    setShowRecordingModal(true);
                  }}
                  style={{ marginTop: "var(--space-3)" }}
                >
                  Attach Recording
                </Button>
              </div>
            )}

            {lecture?.attendance && lecture.attendance.length > 0 && (
              <div className="card-section">
                <h3>Attendance</h3>
                <div className="attendance-list">
                  {lecture.attendance.map((att, idx) => (
                    <div key={idx} className="attendance-item">
                      <div>
                        <strong>
                          {att.student?.firstName || "Student"}{" "}
                          {att.student?.lastName || ""}
                        </strong>
                        <span className={`status-${att.status}`}>
                          {att.status}
                        </span>
                      </div>
                      {att.notes && <p>{att.notes}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Lecture"
        size="large"
      >
        <form onSubmit={handleEdit}>
          <Select
            label="Module"
            name="module"
            value={formData.module}
            onChange={(e) =>
              setFormData({ ...formData, module: e.target.value })
            }
            options={[
              { value: "", label: "Select Module (Optional)" },
              ...availableModules.map((m) => ({
                value: m._id,
                label: `${m.order}. ${m.title}`,
              })),
            ]}
          />
          <Select
            label="Teacher"
            name="teacher"
            value={formData.teacher}
            onChange={(e) =>
              setFormData({ ...formData, teacher: e.target.value })
            }
            options={[
              { value: "", label: "Select Teacher (Optional)" },
              ...availableTeachers.map((t) => ({
                value: t._id,
                label: `${t.firstName} ${t.lastName}`.trim() || t.email,
              })),
            ]}
          />
          <FormInput
            label="Topic"
            name="topic"
            value={formData.topic}
            onChange={(e) =>
              setFormData({ ...formData, topic: e.target.value })
            }
            required
          />
          <FormInput
            label="Description"
            name="description"
            type="textarea"
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <FormInput
            label="Start Time"
            name="startTime"
            type="datetime-local"
            value={formData.startTime}
            onChange={(e) =>
              setFormData({ ...formData, startTime: e.target.value })
            }
            required
          />
          <FormInput
            label="End Time"
            name="endTime"
            type="datetime-local"
            value={formData.endTime}
            onChange={(e) =>
              setFormData({ ...formData, endTime: e.target.value })
            }
            required
          />
          <FormInput
            label="Meeting Link"
            name="meetingLink"
            value={formData.meetingLink}
            onChange={(e) =>
              setFormData({ ...formData, meetingLink: e.target.value })
            }
            placeholder="https://meet.google.com/..."
          />
          <FormInput
            label="Recording Link"
            name="recordingLink"
            value={formData.recordingLink}
            onChange={(e) =>
              setFormData({ ...formData, recordingLink: e.target.value })
            }
            placeholder="https://youtube.com/..."
          />
          <Select
            label="Status"
            name="status"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            options={[
              { value: "scheduled", label: "Scheduled" },
              { value: "live", label: "Live" },
              { value: "completed", label: "Completed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Update Lecture
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Lecture"
        size="medium"
      >
        <p>Are you sure you want to delete this lecture? This action cannot be undone.</p>
        <div className="modal-actions">
          <Button
            variant="secondary"
            type="button"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Recording Modal */}
      <Modal
        isOpen={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
        title="Attach Recording"
        size="medium"
      >
        <form onSubmit={handleAttachRecording}>
          <FormInput
            label="Recording Link"
            name="recordingLink"
            value={recordingLink}
            onChange={(e) => setRecordingLink(e.target.value)}
            placeholder="https://youtube.com/..."
            required
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowRecordingModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Attach Recording
            </Button>
          </div>
        </form>
      </Modal>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Lecture Status"
        size="medium"
      >
        <form onSubmit={handleStatusUpdate}>
          <Select
            label="Status"
            name="status"
            value={statusUpdate}
            onChange={(e) => setStatusUpdate(e.target.value)}
            options={[
              { value: "scheduled", label: "Scheduled" },
              { value: "live", label: "Live" },
              { value: "completed", label: "Completed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
            required
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowStatusModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={editLoading}>
              Update Status
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default LectureDetail;

